import React, { useEffect } from 'react';

import useTranslate from '@src/i18n/useTranslate';
import { Order, SaleChange } from '@src/core/buyerApiClient';
import { useOrderDetails } from '@src/features/orders/details';
import { OrderDetailsFooter } from '@src/features/orders/core/resolveOrderDetailsFooter';
import { Container, Wrapper } from '@src/components/Account/shared';

import Reservation from './Reservation';
import HeaderContent from './HeaderContent';
import { track } from '@src/services/analytics';

import {
  Button,
  FooterInfo,
  GoBackButton,
  HeaderTitle,
  PackageContainer,
  PackageHeader,
  PackageItemList,
  PackageTitle,
  ReservationWrapper,
} from './styles';

type Props = {
  order: Order;
  onClose?: () => void;
  onOrderCheckout?: (order: Order) => void;
  onSaleChangeCheckout?: (saleChange: SaleChange) => void;
};

const SaleDisplay = (props: Props) => {
  const { order, onClose, onOrderCheckout = () => {}, onSaleChangeCheckout = () => {} } = props;
  const { i18n } = useTranslate();
  const { packageGroups, individualItems, hasMultipleVouchers, footer, originalAmountCents } = useOrderDetails(order);

  useEffect(() => {
    track('saleAccess', { orderId: order.id });
  }, []);

  return (
    <Wrapper>
      <Container style={{ maxWidth: 876 }}>
        <HeaderTitle>{i18n('account_myPurchases')}</HeaderTitle>
        <HeaderContent order={order} originalAmountCents={originalAmountCents} />

        {packageGroups.map((packageGroupItem) => (
          <PackageContainer key={packageGroupItem.productPackage.id}>
            <PackageHeader>
              <PackageTitle>
                {i18n('account_sale_package_title', {
                  quantity: String(packageGroupItem.items.length),
                  packageName: packageGroupItem.productPackage.name,
                })}
              </PackageTitle>
            </PackageHeader>

            <PackageItemList>
              {packageGroupItem.items.map((item) => (
                <Reservation
                  isPackageItem
                  disableReschedule
                  order={order}
                  saleItem={item}
                  key={item.id}
                  hasMultipleVouchers={hasMultipleVouchers}
                  onSaleChangeCheckout={onSaleChangeCheckout}
                />
              ))}
            </PackageItemList>
          </PackageContainer>
        ))}

        {individualItems.length > 0 ? (
          <ReservationWrapper>
            {individualItems.map((item) => (
              <Reservation
                order={order}
                saleItem={item}
                key={item.id}
                hasMultipleVouchers={hasMultipleVouchers}
                onSaleChangeCheckout={onSaleChangeCheckout}
              />
            ))}
          </ReservationWrapper>
        ) : null}

        <FooterInfo>
          {footer === OrderDetailsFooter.UnavailableReservation ? <p>{i18n('sale_noAvailableReservation')}</p> : null}
          {footer === OrderDetailsFooter.Checkout ? (
            <Button onClick={() => onOrderCheckout(order)}>{i18n('account_sale_checkout')}</Button>
          ) : null}
          {onClose ? <GoBackButton onClick={onClose}>{i18n('sale_goBack')}</GoBackButton> : null}
        </FooterInfo>
      </Container>
    </Wrapper>
  );
};

export default SaleDisplay;
